// src/pages/FinancialQAPage.jsx
import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, XCircle, TrendingUp, Database, DollarSign } from 'lucide-react';

// Changed to default export with consistent naming
const FinancialQAPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientId] = useState(1);

  useEffect(() => {
    fetchFinancialQA();
  }, [clientId]);

  const fetchFinancialQA = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      console.log('Fetching financial QA data for client:', clientId);
      
      const response = await fetch(`/api/v2/useCases/financial-qa?clientId=${clientId}`);
      const result = await response.json();
      
      console.log('Financial QA response:', result);
      
      if (result.success) {
        setData(result);
      } else {
        setError(result.message || 'Failed to fetch data');
      }
    } catch (err) {
      console.error('Error fetching financial QA:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getConfidenceColor = (confidence) => {
    switch(confidence) {
      case 'High': return 'text-green-600';
      case 'Medium': return 'text-yellow-600';
      case 'Low': return 'text-red-600';
      default: return 'text-gray-500';
    }
  };

  const getSourceBadge = (source) => {
    switch(source) {
      case 'Calculated from ROI steps':
        return <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800">Calculated</span>;
      case 'From ROI assumptions':
        return <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">Assumptions</span>;
      case 'Category default (fallback)':
        return <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-800">Default</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800">No Data</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading financial data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <XCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-800">Error: {error}</p>
          </div>
          <button 
            onClick={fetchFinancialQA}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Check if we have data and it's in the expected structure
  if (!data || !data.data) {
    return (
      <div className="p-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
            <p className="text-yellow-800">No data available. The API returned an empty response.</p>
          </div>
          <button 
            onClick={fetchFinancialQA}
            className="mt-3 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  const { summary, data: useCases, diagnostic_info } = data;

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Financial QA Dashboard</h1>
        <p className="text-gray-600">Comprehensive view of ROI calculations and data sources</p>
        <p className="text-sm text-gray-500 mt-2">Client ID: {clientId} | Last updated: {new Date().toLocaleString()}</p>
      </div>

      {/* System Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Financial Snapshot View</h3>
            {summary?.financial_snapshot_status === 'Working' ? 
              <CheckCircle className="h-5 w-5 text-green-500" /> :
              <AlertCircle className="h-5 w-5 text-yellow-500" />
            }
          </div>
          <p className={`text-lg font-semibold ${
            summary?.financial_snapshot_status === 'Working' ? 'text-green-600' : 'text-yellow-600'
          }`}>
            {summary?.financial_snapshot_status || 'Unknown'}
          </p>
          {summary?.snapshot_sample && (
            <p className="text-xs text-gray-500 mt-1">
              {summary.snapshot_sample.length} samples retrieved
            </p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Data Coverage</h3>
            <Database className="h-5 w-5 text-blue-500" />
          </div>
          <p className="text-lg font-semibold text-gray-900">
            {summary?.use_cases_with_revenue_data || 0} / {summary?.total_use_cases || 0}
          </p>
          <p className="text-xs text-gray-500 mt-1">Use cases with revenue data</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Data Sources</h3>
            <TrendingUp className="h-5 w-5 text-indigo-500" />
          </div>
          <div className="space-y-1">
            <p className="text-xs">
              <span className="font-medium">Calculated:</span> {summary?.revenue_sources_breakdown?.calculated || 0}
            </p>
            <p className="text-xs">
              <span className="font-medium">Assumptions:</span> {summary?.revenue_sources_breakdown?.assumptions || 0}
            </p>
            <p className="text-xs">
              <span className="font-medium">Defaults:</span> {summary?.revenue_sources_breakdown?.fallback || 0}
            </p>
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      {summary && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Summary Statistics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Total Use Cases</p>
              <p className="text-2xl font-bold text-gray-900">{summary.total_use_cases || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">With Calculations</p>
              <p className="text-2xl font-bold text-blue-600">{summary.use_cases_with_calculations || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">With Assumptions</p>
              <p className="text-2xl font-bold text-green-600">{summary.use_cases_with_assumptions || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">With Profiles</p>
              <p className="text-2xl font-bold text-purple-600">{summary.use_cases_with_profiles || 0}</p>
            </div>
          </div>

          {/* Top 5 by Revenue */}
          {summary.top_5_by_revenue && summary.top_5_by_revenue.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Top 5 by Revenue Potential</h3>
              <div className="space-y-2">
                {summary.top_5_by_revenue.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium">{item.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{formatCurrency(item.revenue)}</span>
                      {getSourceBadge(item.source)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detailed Use Case Table */}
      {useCases && useCases.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              All Use Cases - Financial Data ({useCases.length} items)
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Use Case
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Annual Revenue
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payback
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Confidence
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {useCases.map((uc, idx) => (
                  <tr key={uc.use_case_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {uc.rank !== 999 ? `#${uc.rank}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div>
                        <p className="font-medium">{uc.use_case_name}</p>
                        <p className="text-xs text-gray-500">ID: {uc.use_case_id}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {uc.category_name || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div>
                        <p className="font-semibold">{uc.total_score?.toFixed(1) || 0}</p>
                        <p className="text-xs text-gray-500">
                          V:{uc.value_score?.toFixed(1) || 0} E:{uc.implementation_score?.toFixed(1) || 0}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-green-600">
                      {formatCurrency(uc.annual_revenue_potential)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {getSourceBadge(uc.revenue_source)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {uc.payback_months ? `${uc.payback_months} mo` : '—'}
                    </td>
                    <td className={`px-4 py-3 text-sm font-medium ${getConfidenceColor(uc.confidence_level)}`}>
                      {uc.confidence_level || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex gap-1">
                        <span className={`inline-flex items-center justify-center w-6 h-6 text-xs rounded ${
                          uc.has_calculations === 'Yes' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                        }`} title="Has Calculations">
                          C
                        </span>
                        <span className={`inline-flex items-center justify-center w-6 h-6 text-xs rounded ${
                          uc.has_assumptions === 'Yes' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
                        }`} title="Has Assumptions">
                          A
                        </span>
                        <span className={`inline-flex items-center justify-center w-6 h-6 text-xs rounded ${
                          uc.has_profile === 'Yes' ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-400'
                        }`} title="Has Profile">
                          P
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {(!useCases || useCases.length === 0) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
            <div>
              <p className="text-yellow-800 font-medium">No use case data available</p>
              <p className="text-sm text-yellow-600 mt-1">
                This could mean the financial snapshot view hasn't been created yet or there's no data for this client.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Diagnostic Information */}
      {diagnostic_info && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Diagnostic Information</h3>
          <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
            <div>
              <p className="font-medium">Tables Checked:</p>
              <ul className="ml-4 mt-1">
                {diagnostic_info.tables_checked?.map(table => (
                  <li key={table}>• {table}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-medium">Views Checked:</p>
              <ul className="ml-4 mt-1">
                {diagnostic_info.views_checked?.map(view => (
                  <li key={view}>• {view}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Make sure to export as default
export default FinancialQAPage;